import { useEffect, useMemo, useRef, useState } from 'react'
import { UserCircle, Search, CornerDownLeft, type LucideIcon } from 'lucide-react'
import { navigate } from '../router.js'
import { paletteDestinations } from '../navigation/site-navigation.js'
import { useOverlay } from './overlays.js'

/**
 * COMMAND PALETTE (LOT 5.5) — la recherche centrale de Gestion.
 *
 * Périmètre volontaire de ce lot : NAVIGATION et destinations — pas de
 * recherche d'entités (utilisateurs, audit, alertes). Tant que
 * l'enforcement est désactivé, un tel endpoint serait interrogeable
 * anonymement : l'extension aux entités viendra APRÈS le LOT 6.
 * Les destinations sont donc un catalogue statique côté client, filtré par
 * les permissions de la session quand elles existent — le backend restant
 * seul décideur au moment d'y accéder.
 */

export interface Destination {
  label: string
  section: string
  path: string
  Icon: LucideIcon
  keywords: string
  /** Permission optionnelle : masque la destination si la session est
   *  authentifiée ET ne l'a pas. Sans session (enforcement off), tout est
   *  visible — comme la sidebar. */
  permission?: string
  /** L'un de ces droits suffit aussi. */
  alsoPermissions?: string[]
}

/* IA V2 — la palette CONSOMME le registre central : plus de liste locale.
   Les PLANNED sans page n'existent pas ici (une action doit ouvrir
   quelque chose) ; Mon profil reste une entrée de la zone compte. */
export const DESTINATIONS: Destination[] = [
  ...paletteDestinations().map((d) => ({
    label: d.label, section: d.section, path: d.path, Icon: d.icon,
    keywords: d.keywords, ...(d.permission ? { permission: d.permission } : {}),
    ...(d.alsoPermissions ? { alsoPermissions: d.alsoPermissions } : {}),
  })),
  { label: 'Mon profil', section: 'Compte', path: '/profile', Icon: UserCircle, keywords: 'profil compte mfa totp mot de passe sessions codes secours' },
  { label: 'Mon espace — temps de travail', section: 'Compte', path: '/moi', Icon: UserCircle, keywords: 'mon espace planning heures compteurs demandes conges disponibilites pointage pin' },
]

const fold = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

const RECENTS_KEY = 'gestion.palette-recents'
function readRecents(): string[] {
  try { return JSON.parse(localStorage.getItem(RECENTS_KEY) ?? '[]') as string[] } catch { return [] }
}
function pushRecent(path: string): void {
  try {
    const r = [path, ...readRecents().filter((p) => p !== path)].slice(0, 5)
    localStorage.setItem(RECENTS_KEY, JSON.stringify(r))
  } catch { /* stockage indisponible */ }
}

export function CommandPalette(props: {
  open: boolean
  onClose: () => void
  permissions: string[]
  authenticated: boolean
}) {
  const [q, setQ] = useState('')
  const [idx, setIdx] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const openerRef = useRef<HTMLElement | null>(null)

  useOverlay(props.open, props.onClose, { lockScroll: true })

  useEffect(() => {
    if (props.open) {
      openerRef.current = document.activeElement as HTMLElement | null
      setQ(''); setIdx(0)
      // Le focus est posé par autoFocus au montage ; cette relance couvre les
      // réouvertures où l'élément existe déjà.
      setTimeout(() => inputRef.current?.focus(), 0)
    } else {
      openerRef.current?.focus()
      openerRef.current = null
    }
  }, [props.open])

  const visible = useMemo(() => DESTINATIONS.filter((d) =>
    !d.permission || !props.authenticated || props.permissions.includes(d.permission)
      || (d.alsoPermissions ?? []).some((p) => props.permissions.includes(p)),
  ), [props.permissions, props.authenticated])

  const results = useMemo(() => {
    const needle = fold(q.trim())
    if (!needle) {
      const recents = readRecents()
        .map((p) => visible.find((d) => d.path === p))
        .filter((d): d is Destination => !!d)
        .map((d) => ({ ...d, section: 'Récents' }))
      const rest = visible.filter((d) => !recents.some((r) => r.path === d.path))
      return [...recents, ...rest]
    }
    return visible
      .map((d) => {
        const label = fold(d.label)
        const hay = `${label} ${fold(d.keywords)}`
        let score = -1
        if (label.startsWith(needle)) score = 0
        else if (label.includes(needle)) score = 1
        else if (hay.includes(needle)) score = 2
        else if (needle.split(/\s+/).every((w) => hay.includes(w))) score = 3
        return { d, score }
      })
      .filter((x) => x.score >= 0)
      .sort((a, b) => a.score - b.score)
      .map((x) => x.d)
  }, [q, visible])

  useEffect(() => { setIdx(0) }, [q])

  if (!props.open) return null

  const go = (d: Destination) => {
    pushRecent(d.path)
    props.onClose()
    navigate(d.path)
  }

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setIdx((i) => Math.min(results.length - 1, i + 1)) }
    if (e.key === 'ArrowUp') { e.preventDefault(); setIdx((i) => Math.max(0, i - 1)) }
    if (e.key === 'Enter' && results[idx]) { e.preventDefault(); go(results[idx]!) }
    if (e.key === 'Tab') e.preventDefault() // focus trap : tout vit dans le champ
  }

  let lastSection = ''
  return (
    <div className="lc-palette-backdrop" onClick={props.onClose} role="presentation">
      <div
        className="lc-palette"
        role="dialog" aria-modal="true" aria-label="Recherche globale"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKey}
      >
        <div className="lc-palette__field">
          <Search size={19} aria-hidden="true" />
          <input
            ref={inputRef}
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Où voulez-vous aller ?"
            aria-label="Rechercher une destination"
            role="combobox" aria-expanded="true" aria-controls="lc-palette-list"
            aria-activedescendant={results[idx] ? `lcp-${idx}` : undefined}
          />
          <span className="lc-palette__esc">Échap</span>
        </div>
        <div className="lc-palette__list" id="lc-palette-list" role="listbox">
          {results.length === 0 ? (
            <p className="lc-palette__empty">
              Aucune destination ne correspond. La recherche dans les données
              (comptes, alertes, audit) arrivera avec l’activation de
              l’authentification.
            </p>
          ) : results.map((d, i) => {
            const head = d.section !== lastSection ? d.section : null
            lastSection = d.section
            return (
              <div key={d.path + d.section}>
                {head ? <div className="lc-palette__section">{head}</div> : null}
                <div
                  id={`lcp-${i}`}
                  role="option" aria-selected={i === idx}
                  className={`lc-palette__item${i === idx ? ' lc-palette__item--active' : ''}`}
                  onMouseEnter={() => setIdx(i)}
                  onClick={() => go(d)}
                >
                  <span className="lc-palette__icon"><d.Icon size={17} /></span>
                  <span className="lc-palette__label">{d.label}</span>
                  <span className="lc-palette__path">{d.path}</span>
                  {i === idx ? <CornerDownLeft size={14} className="lc-palette__enter" aria-hidden="true" /> : null}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
